import React, { Component } from 'react';
import { FiAlertTriangle, FiRefreshCw, FiHome } from 'react-icons/fi';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to the console
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    
    this.setState(prevState => ({
      error: error,
      errorInfo: errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Log to error tracking service (future enhancement)
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    try {
      const errorData = {
        message: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      // Future: Send to backend
      // fetch('/api/v1/logs/error', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorData)
      // });
    } catch (loggingError) {
      console.error('Failed to log error:', loggingError);
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorCount } = this.state;
      // Use Vite's import.meta.env for environment detection
      // In production builds, import.meta.env.DEV is false and import.meta.env.PROD is true
      const isDevelopment = typeof import.meta !== 'undefined' && import.meta.env?.DEV === true;
      const showDetails = isDevelopment;

      // Custom fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-red-100 dark:bg-red-900/20 rounded-full">
                <FiAlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">
              Oops! Something went wrong
            </h2>

            {/* Message */}
            <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
              We encountered an unexpected error. Don't worry, our team has been notified.
            </p>

            {/* Error Count Warning */}
            {errorCount > 2 && (
              <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Note:</strong> This error has occurred {errorCount} times. 
                  If the problem persists, please contact support.
                </p>
              </div>
            )}

            {/* Error Details (Development Only) */}
            {showDetails && error && (
              <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-auto max-h-60">
                <details>
                  <summary className="cursor-pointer font-medium text-gray-900 dark:text-white mb-2 hover:text-red-600 dark:hover:text-red-400 text-sm">
                    Show Error Details (Development Mode)
                  </summary>
                  <div className="mt-3 space-y-3">
                    <div>
                      <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1 text-sm">Error Message:</p>
                      <code className="block text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded text-xs">
                        {error.toString()}
                      </code>
                    </div>
                    {error.stack && (
                      <div>
                        <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1 text-sm">Stack Trace:</p>
                        <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto bg-gray-50 dark:bg-gray-800 p-2 rounded max-h-40 overflow-y-auto">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                    {errorInfo && errorInfo.componentStack && (
                      <div>
                        <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1 text-sm">Component Stack:</p>
                        <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto bg-gray-50 dark:bg-gray-800 p-2 rounded max-h-40 overflow-y-auto">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
              >
                <FiRefreshCw className="w-5 h-5" />
                <span>Try Again</span>
              </button>
              <button
                onClick={this.handleReload}
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-md hover:shadow-lg"
              >
                <FiRefreshCw className="w-5 h-5" />
                <span>Reload Page</span>
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                <FiHome className="w-5 h-5" />
                <span>Go Home</span>
              </button>
            </div>

            {/* Help Text */}
            <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Need help? Contact us at{' '}
              <a href="mailto:support@tasklina.com" className="text-blue-600 dark:text-blue-400 hover:underline">
                support@tasklina.com
              </a>
            </p>
          </div>
        </div>
      );
    }

    // If there's no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
